import { DataSource } from '@client/core/data/concerns/data-source';
import { fromJson, JsonResponse } from '@client/core/data/concerns/parses-from-json';
import { Pagination } from '@client/core/http/pagination';
import {
  HttpResponse,
  HttpResponseWithMeta,
  PaginationRequestParams,
  PaginationResponse,
} from '@client/core/http/types';
import { ActivityLog } from '@client/features/activity-log/models/activity-log';
import { ActivityLogProperties } from '@client/features/activity-log/types';
import { CreateActivityLogEntryUpdateParams } from '@client/features/activity-log/use-cases/create-activity-log-entry-update';
import { FindActivityLogByIdParams } from '@client/features/activity-log/use-cases/find-activity-log-by-id';
import { Project } from '@client/features/project/models/project';

interface FindAllActivityLogsParams extends PaginationRequestParams {
  sort_by: 'newest' | 'oldest';
}

export class ActivityLogDataSource extends DataSource {
  /**
   * @see app/Http/Controllers/ActivityLog/CreateActivityLogUpdateController.php
   * @deprecated This feature is being removed from the software
   */
  async createActivityLogUpdate(
    ownerId: number,
    projectId: Project['uuid'],
    params: Pick<CreateActivityLogEntryUpdateParams, 'updateText'>,
  ): Promise<ActivityLog<ActivityLogProperties.ProjectActivityLogUpdate>> {
    const response = await this.httpClient.post<HttpResponse<JsonResponse>>(
      `/workspace/${ownerId}/project/${projectId}/activity-log-update`,
      {
        update_text: params.updateText,
      },
    );

    return fromJson(ActivityLog<ActivityLogProperties.ProjectActivityLogUpdate>, response.data.data);
  }

  /**
   * @see app/Http/Controllers/ActivityLog/ActivityLogController.php
   */
  async findAll(
    ownerId: number,
    projectId: Project['uuid'],
    params: FindAllActivityLogsParams,
  ): Promise<PaginationResponse<ActivityLog[]>> {
    const response = await this.httpClient.get<HttpResponseWithMeta<JsonResponse[], Pagination>>(
      `/workspace/${ownerId}/project/${projectId}/activity-log`,
      {
        params: {
          ...params,
          page: params?.page ?? 1,
        },
      },
    );

    return {
      data: response.data.data.map((activityLog) => fromJson(ActivityLog, activityLog)),
      pagination: fromJson(Pagination, response.data.meta),
    };
  }

  /**
   * @see app/Http/Controllers/ActivityLog/WorkspaceActivityLogController.php
   */
  async findAllByWorkspace(
    ownerId: number,
    params: FindAllActivityLogsParams,
  ): Promise<PaginationResponse<ActivityLog[]>> {
    const response = await this.httpClient.get<HttpResponseWithMeta<JsonResponse[], Pagination>>(
      `/workspace/${ownerId}/activity-log`,
      {
        params: {
          ...params,
          page: params?.page ?? 1,
        },
      },
    );

    return {
      data: response.data.data.map((activityLog) => fromJson(ActivityLog, activityLog)),
      pagination: fromJson(Pagination, response.data.meta),
    };
  }

  /**
   * @see app/Http/Controllers/ActivityLog/GetActivityLogController.php
   */
  async findById(params: FindActivityLogByIdParams): Promise<ActivityLog> {
    const response = await this.httpClient.get<HttpResponse<JsonResponse>>(
      `/workspace/${params.ownerId}/activity-log/${params.activityLogId}`,
    );

    return fromJson(ActivityLog, response.data.data);
  }
}
